import { useState } from 'react'
import { FlaskConical, Loader2, Play, RotateCcw } from 'lucide-react'
import { useStartSimulatedCopilotCall, useListScenarios } from '../api/copilotSandbox'
import { LiveTranscriptPanel } from '../components/copilot/LiveTranscriptPanel'
import { SentimentGauge } from '../components/copilot/SentimentGauge'
import { InsightNotebook } from '../components/copilot/InsightNotebook'
import { LiveCallbackCard } from '../components/copilot/LiveCallbackCard'
import { CoachingCardStack } from '../components/copilot/CoachingCardStack'
import { useCopilotStore } from '../stores/copilotStore'

type RunState = 'idle' | 'running' | 'done' | 'error'

export function CopilotSandboxPage() {
  const { data: scenarios, isLoading, error } = useListScenarios()
  const startSim = useStartSimulatedCopilotCall()
  const resetCopilot = useCopilotStore((s) => s.reset)

  const [runState, setRunState] = useState<RunState>('idle')
  const [activeCallSid, setActiveCallSid] = useState<string | null>(null)
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [simError, setSimError] = useState<string | null>(null)

  const isRunning = runState === 'running'

  const handleStart = async (scenarioName: string) => {
    setRunState('running')
    setSimError(null)
    try {
      const data = await startSim.mutateAsync(scenarioName)
      setActiveCallSid(data.call_sid)
      setActiveScenario(data.scenario)
      setRunState('done')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start simulation'
      setSimError(message)
      setRunState('error')
    }
  }

  const handleReset = () => {
    if (activeCallSid) resetCopilot(activeCallSid)
    setActiveCallSid(null)
    setActiveScenario(null)
    setRunState('idle')
    setSimError(null)
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-text-primary">
          <FlaskConical className="h-5 w-5 text-primary" />
          Copilot Sandbox
        </h1>
        {(runState === 'done' || runState === 'error') && (
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center gap-2 rounded border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-elevated"
          >
            <RotateCcw className="h-4 w-4" />
            New simulation
          </button>
        )}
      </header>

      <p className="text-sm text-text-secondary">
        Replay a scripted conversation through the copilot analyzer without dialing any real number.
        The transcript, sentiment, insights, and coaching cards stream in exactly as they would on a
        live call.
      </p>

      {simError && (
        <div className="rounded border border-red-500/40 bg-red-500/5 px-4 py-2 text-sm text-red-700">
          {simError}
        </div>
      )}

      {runState === 'idle' && (
        <div className="rounded-lg border border-border bg-surface-card p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
            Choose a scenario
          </h2>
          {isLoading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading scenarios…
            </div>
          )}
          {error && (
            <p className="mt-4 text-sm text-text-secondary">
              Sandbox not available. Enable{' '}
              <code className="rounded bg-surface px-1 py-0.5 text-xs">COPILOT_SANDBOX_ENABLED=true</code>{' '}
              on the backend to use this feature.
            </p>
          )}
          {scenarios && scenarios.length === 0 && (
            <p className="mt-4 text-sm text-text-secondary">No scenarios found.</p>
          )}
          {scenarios && scenarios.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {scenarios.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => handleStart(s.name)}
                  disabled={isRunning}
                  className="flex flex-col items-start gap-2 rounded-lg border border-border bg-surface p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <Play className="h-4 w-4 text-primary" />
                    {s.name}
                  </span>
                  {s.description && (
                    <span className="text-xs text-text-secondary line-clamp-3">
                      {s.description.trim()}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {runState === 'running' && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting simulation…
        </div>
      )}

      {(runState === 'done' || runState === 'error') && activeCallSid && (
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-sm text-text-secondary">
            <span>
              Scenario: <span className="font-medium text-text-primary">{activeScenario}</span>
            </span>
            <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-mono text-primary">
              {activeCallSid}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
            <div className="min-h-[400px] lg:h-[calc(100vh-14rem)]">
              <LiveTranscriptPanel callSid={activeCallSid} />
            </div>
            <div className="space-y-4">
              <LiveCallbackCard callSid={activeCallSid} />
              <SentimentGauge callSid={activeCallSid} />
              <CoachingCardStack callSid={activeCallSid} />
              <InsightNotebook callSid={activeCallSid} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
